import React, { useRef, useState } from "react";
import { ContextMenu } from "primereact/contextmenu";
import useTabId from "hooks/dashboard/useTabId";
import { T } from "services/lang/LanguageService";
import { DashboardLayoutType, IDashboardComponent } from "api/model/dashboard/DashboardComponent";
import useDashboardStore from "store/dashboard-store";
import { v4 as uuidv4 } from "uuid";
import { easasConfirmDialog } from "components/fragments/easas-global-fragment/EasasGlobalConfirmDialog";
import DashboardComponentCard from "./GroupComponentCard";
import DashboardComponentConfiguratorNew, {
  ConfiguratorModes,
} from "./GroupComponentConfiguratorNew";
import { updateDashboardTabs } from "../../utils";
import MoveGroupOrComponent from "./MoveGroupOrComponent";

interface DashboardComponentProps {
  groupId: string;
  componentId: string;
  component: IDashboardComponent;
  layoutType: DashboardLayoutType;
  setIsGridLayoutEditAvailable?: (isGridLayoutEditAvailable: boolean) => void;
}

enum DialogType {
  MOVE = "MOVE",
  EDITOR = "EDITOR",
}

const DashboardComponent = ({
  groupId,
  componentId,
  component,
  layoutType,
  setIsGridLayoutEditAvailable,
}: DashboardComponentProps) => {
  const cm = useRef<ContextMenu>(null);
  const isEditMode = useDashboardStore(state => state.isEditMode);
  const dashboard = useDashboardStore(state => state.dashboard);
  const updateComponent = useDashboardStore(state => state.updateComponent);
  const deleteComponent = useDashboardStore(state => state.removeComponent);
  const tabId = useTabId();
  const [isConfiguratorVisible, setIsConfiguratorVisible] = useState<boolean>(false);
  const [isMoveComponentVisible, setIsMoveComponentVisible] = useState<boolean>(false);

  const handleDeleteComponent = () => {
    easasConfirmDialog({
      icon: "pi pi-exclamation-triangle",
      header: T("action.confirm-action"),
      message: T("confirm-dialog.are-you-sure-you-want-to-delete-this-component"),
      confirmedCallback: () => {
        deleteComponent(groupId, componentId, tabId);
      },
    });
  };

  const handleDuplicateComponent = () => {
    if (!dashboard) return;
    const currentGroup = dashboard.dashboardTabsJs?.[tabId]?.componentGroups?.find(
      group => group.id === groupId
    );
    const componentToDuplicate = currentGroup?.components?.find(
      currentComponent => currentComponent.id === componentId
    );

    if (!currentGroup || !componentToDuplicate) return;

    const componentToDuplicateWithNewId = JSON.parse(JSON.stringify(componentToDuplicate));
    componentToDuplicateWithNewId.id = uuidv4();

    if (componentToDuplicateWithNewId.type === "Scada") {
      componentToDuplicateWithNewId.config.id = uuidv4();
    }

    currentGroup.components.push(componentToDuplicateWithNewId);

    const updatedTabs = dashboard.dashboardTabsJs.map((tab, index) =>
      index === tabId
        ? {
            ...tab,
            componentGroups: tab.componentGroups.map(group =>
              group.id === groupId ? currentGroup : group
            ),
          }
        : tab
    );

    updateDashboardTabs(dashboard, updatedTabs);
  };

  const items = [
    {
      label: T("action.edit-config"),
      icon: "pi pi-pencil",
      command: () => {
        handleDialogVisibilityChange(true, DialogType.EDITOR);
      },
    },
    {
      label: T("action.duplicate-component"),
      icon: "pi pi-clone",
      command: () => {
        easasConfirmDialog({
          icon: "pi pi-clone",
          acceptClassName: "p-button-success",
          header: T("action.confirm-action"),
          message: T("confirm-dialog.are-you-sure-you-want-to-duplicate-this-component"),
          confirmedCallback: () => {
            handleDuplicateComponent();
          },
        });
      },
    },
    {
      label: T("action.delete"),
      icon: "pi pi-times",
      command: () => {
        handleDeleteComponent();
      },
    },
    {
      label: T("action.move-component"),
      icon: "pi pi-arrow-right",
      command: () => {
        handleDialogVisibilityChange(true, DialogType.MOVE);
      },
    },
  ];

  const handleComponentChange = (newConfig: IDashboardComponent) => {
    updateComponent(groupId, componentId, newConfig, tabId);
    handleDialogVisibilityChange(false, DialogType.EDITOR);
  };

  const handleDialogVisibilityChange = (isVisible: boolean, dialogType: DialogType) => {
    if (dialogType === DialogType.MOVE) {
      setIsMoveComponentVisible(isVisible);
    } else {
      setIsConfiguratorVisible(isVisible);
    }
    if (setIsGridLayoutEditAvailable) setIsGridLayoutEditAvailable(!isVisible);
  };

  return (
    <div className="h-full">
      {isEditMode && <ContextMenu model={items} ref={cm} />}
      <div onContextMenu={e => cm.current?.show(e)} className="h-full m-1">
        <DashboardComponentCard component={component} tabId={tabId} groupId={groupId} />
      </div>
      {isConfiguratorVisible && (
        <DashboardComponentConfiguratorNew
          isDialogVisible={isConfiguratorVisible}
          mode={ConfiguratorModes.EDIT}
          setIsDialogVisible={(isDialogVisible: boolean) =>
            handleDialogVisibilityChange(isDialogVisible, DialogType.EDITOR)
          }
          component={component}
          onComponentChange={handleComponentChange}
          layoutType={layoutType}
        />
      )}
      {isMoveComponentVisible && dashboard && (
        <MoveGroupOrComponent
          isVisible={isMoveComponentVisible}
          setIsVisible={(isVisible: boolean) =>
            handleDialogVisibilityChange(isVisible, DialogType.MOVE)
          }
          component={component}
          tabId={tabId}
          groupId={groupId}
          componentId={componentId}
          dashboardId={dashboard.id}
          isComponent
        />
      )}
    </div>
  );
};

function areEqual(
  prev: Readonly<DashboardComponentProps>,
  next: Readonly<DashboardComponentProps>
): boolean {
  return (
    prev.component.id === next.component.id &&
    JSON.stringify(prev.component.config) === JSON.stringify(next.component.config) &&
    prev.groupId === next.groupId &&
    prev.componentId === next.componentId &&
    prev.layoutType === next.layoutType
  );
}

export default React.memo(DashboardComponent, areEqual);
